import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import StarOutline from "@mui/icons-material/StarOutline";
import NewReleasesOutlined from "@mui/icons-material/NewReleasesOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import TitleInformationForm from "layouts/publish-here/components/PublishSteps/TitleInformationForm";
import { adminPublications } from "services/adminPublications";
import { bookEditingPayload } from "shared/bookEditingPayload";

export default function PublicationsAdmin({ user }) {
  const [menu, setMenu] = useState(null);
  const [options, setOptions] = useState(null);
  const [menuError, setMenuError] = useState("");
  const [menuLoading, setMenuLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState({ books: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [hasBlockingOrders, setHasBlockingOrders] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [orderPage, setOrderPage] = useState(1);
  const [orderRevision, setOrderRevision] = useState(0);
  const [relatedOrders, setRelatedOrders] = useState({
    loading: false,
    orders: [],
    error: "",
    hasMore: false,
  });
  const [copyNotice, setCopyNotice] = useState(null);
  const [code, setCode] = useState("");
  const [dialogError, setDialogError] = useState("");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    adminPublications(`?${new URLSearchParams({ search, page })}`)
      .then((data) => {
        if (active) setResult(data);
      })
      .catch((failure) => {
        if (active) setError(failure.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [search, page, revision]);
  useEffect(() => {
    if (!menu || menu.book.deleting || !menu.book.trayProductId) return undefined;
    let active = true;
    setMenuLoading(true);
    setMenuError("");
    setOptions(null);
    adminPublications(`/${menu.book.id}/store-options`)
      .then((data) => {
        if (active) setOptions(data);
      })
      .catch((failure) => {
        if (active) setMenuError(failure.message);
      })
      .finally(() => {
        if (active) setMenuLoading(false);
      });
    return () => {
      active = false;
    };
  }, [menu]);
  const changeOption = async (field) => {
    setBusy(true);
    setMenuError("");
    try {
      await adminPublications(`/${menu.book.id}/store-options`, "PUT", {
        field,
        value: options.options[field] ? 0 : 1,
        version: options.version,
      });
      setMenu(null);
      setNotice({ severity: "success", text: "Opção da publicação atualizada na loja." });
      setRevision((value) => value + 1);
    } catch (failure) {
      setMenuError(failure.message);
      setOptions(null);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!showOrders || !removing) return undefined;
    let active = true;
    setRelatedOrders({ loading: true, orders: [], error: "", hasMore: false });
    adminPublications(`/${removing.id}/blocking-orders?page=${orderPage}`)
      .then((data) => {
        if (active) setRelatedOrders({ ...data, loading: false, error: "" });
      })
      .catch((failure) => {
        if (active)
          setRelatedOrders({ loading: false, orders: [], error: failure.message, hasMore: false });
      });
    return () => {
      active = false;
    };
  }, [showOrders, removing, orderPage, orderRevision]);
  const openEdit = async (book) => {
    setBusy(true);
    setError("");
    setDialogError("");
    setNotice(null);
    try {
      setEditing((await adminPublications(`/${book.id}`)).book);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };
  const save = async (form) => {
    setBusy(true);
    setDialogError("");
    try {
      const data = await adminPublications(
        `/${editing.id}`,
        "PUT",
        bookEditingPayload(form, editing.version)
      );
      setEditing(null);
      setNotice({
        severity: data.warning ? "warning" : "success",
        text: data.warning || "Publicação atualizada no PSO e na loja.",
      });
      setRevision((value) => value + 1);
    } catch (failure) {
      setDialogError(failure.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    setDialogError("");
    try {
      await adminPublications(`/${removing.id}`, "DELETE", {
        version: removing.version,
        confirmationCode: code,
        deleteFiles: true,
      });
      setRemoving(null);
      setNotice({
        severity: "success",
        text: "Publicação e arquivos excluídos. O produto também foi removido da loja.",
      });
    } catch (failure) {
      setHasBlockingOrders(failure.code === "PUBLICATION_HAS_ACTIVE_ORDERS");
      setDialogError(failure.message);
    } finally {
      setBusy(false);
      setRevision((value) => value + 1);
    }
  };
  return (
    <Box className="publication-admin">
      <Typography variant="overline" color="text.secondary">
        GESTÃO DO CATÁLOGO
      </Typography>
      <Typography variant="h5" mb={1}>
        Publicações
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Encontre obras de todos os autores para editar a ficha ou excluir a publicação do PSO e da
        loja.
      </Typography>
      <Box
        component="form"
        className="publication-admin-search"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setSearch(query.trim());
          setRevision((value) => value + 1);
        }}
      >
        <TextField
          fullWidth
          label="Título, autor, e-mail, ISBN ou código PSO"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          inputProps={{ maxLength: 160 }}
        />
        <Button type="submit" variant="contained" color="success" disabled={loading}>
          Pesquisar
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}
      {loading ? (
        <Box py={5} textAlign="center">
          <CircularProgress aria-label="Carregando publicações" />
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {result.total} publicações encontradas
          </Typography>
          {!result.books.length && (
            <Box className="publication-admin-empty">
              <MenuBookOutlined />
              <Typography variant="h6" mt={2}>
                Nenhuma publicação encontrada
              </Typography>
              <Typography variant="body2">Tente outro título, e-mail ou código.</Typography>
            </Box>
          )}
          <Stack spacing={2}>
            {result.books.map((book) => (
              <Box key={book.id} className="publication-admin-row">
                <Box className="publication-admin-cover">
                  {book.thumbnail ? (
                    <img src={book.thumbnail} alt={`Capa de ${book.title}`} />
                  ) : (
                    <MenuBookOutlined />
                  )}
                </Box>
                <Box className="publication-admin-info">
                  <Stack direction="row" gap={1} flexWrap="wrap" mb={1}>
                    <Chip
                      size="small"
                      label={
                        book.deleting
                          ? "Exclusão pendente"
                          : book.trayStatus === "sync_pending"
                          ? "Sincronização pendente"
                          : book.storeOptions?.available_in_store === 0
                          ? "Oculta na loja"
                          : book.storeOptions?.available === 0
                          ? "Venda indisponível"
                          : "Publicada"
                      }
                      color={
                        book.deleting || book.trayStatus === "sync_pending" ? "warning" : "success"
                      }
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="h6">{book.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {book.author} · {book.ownerEmail || book.ownerName || "Autor sem e-mail"}
                  </Typography>
                  <Typography variant="caption" display="block" mt={1}>
                    PSO: {book.reference} · ISBN: {book.isbn || "Não informado"} · Código na loja:{" "}
                    {book.trayProductId || "Sem vínculo"}
                  </Typography>
                </Box>
                <Stack className="publication-admin-actions" gap={1}>
                  <Button
                    startIcon={<EditOutlined />}
                    variant="outlined"
                    color="success"
                    disabled={busy || book.deleting}
                    onClick={() => openEdit(book)}
                  >
                    Editar publicação
                  </Button>
                  <Button
                    endIcon={<MoreVert />}
                    variant="outlined"
                    color="inherit"
                    sx={{
                      color: "#52616b",
                      borderColor: "#cbd3d9",
                      bgcolor: "#f4f6f8",
                      "&:hover": { borderColor: "#8998a3", bgcolor: "#e9eef2" },
                    }}
                    disabled={busy}
                    aria-haspopup="menu"
                    aria-expanded={menu?.book.id === book.id ? "true" : undefined}
                    onClick={(event) => {
                      setOptions(null);
                      setMenuError("");
                      setMenu({ anchor: event.currentTarget, book });
                    }}
                  >
                    Opções da publicação
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={3}>
            <Button disabled={page === 1 || loading} onClick={() => setPage((value) => value - 1)}>
              Anterior
            </Button>
            <Typography variant="body2">Página {page}</Typography>
            <Button
              disabled={!result.hasMore || loading}
              onClick={() => setPage((value) => value + 1)}
            >
              Próxima
            </Button>
          </Stack>
        </>
      )}
      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => {
          if (!busy) setMenu(null);
        }}
        MenuListProps={{ "aria-label": "Opções da publicação" }}
        PaperProps={{ sx: { width: 320, maxWidth: "calc(100vw - 32px)", borderRadius: 3, mt: 1 } }}
      >
        <Box px={2} py={1}>
          <Typography variant="subtitle2">Opções da publicação</Typography>
          <Typography variant="caption" color="text.secondary">
            Controle a presença desta obra na loja.
          </Typography>
        </Box>
        {menu?.book.deleting && (
          <Box px={2} py={1}>
            <Alert severity="warning">
              Esta obra está com exclusão pendente. Conclua a exclusão para encerrar o processo.
            </Alert>
          </Box>
        )}
        {menu && !menu.book.trayProductId && !menu.book.deleting && (
          <Box px={2} py={1}>
            <Alert severity="info">Esta obra ainda não possui vínculo com a loja.</Alert>
          </Box>
        )}
        {menuLoading && (
          <Box px={2} py={2} role="status">
            <CircularProgress size={18} sx={{ mr: 1 }} />
            Consultando opções…
          </Box>
        )}
        {menuError && (
          <Box px={2} py={1}>
            <Alert severity="error">{menuError}</Alert>
            <Button onClick={() => setMenu((current) => ({ ...current }))} disabled={busy}>
              Consultar novamente
            </Button>
          </Box>
        )}
        {[
          [
            "available_in_store",
            VisibilityOutlined,
            "Ocultar da loja",
            "Mostrar na loja",
            "Visibilidade na vitrine",
          ],
          [
            "available",
            StorefrontOutlined,
            "Indisponibilizar para venda",
            "Disponibilizar para venda",
            "Disponibilidade para compra",
          ],
          ["hot", StarOutline, "Remover dos destaques", "Destacar na loja", "Exibição em destaque"],
          [
            "release",
            NewReleasesOutlined,
            "Remover marca de lançamento",
            "Marcar como lançamento",
            "Identificação de lançamento",
          ],
        ].map(([field, Icon, enabled, disabled, description]) => (
          <MenuItem
            key={field}
            disabled={!options || busy || menuLoading}
            onClick={() => changeOption(field)}
            sx={{ py: 1.2, whiteSpace: "normal" }}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={options?.options[field] ? enabled : disabled}
              secondary={description}
              primaryTypographyProps={{ fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 12 }}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          disabled={busy}
          sx={{ color: "error.main", py: 1.5 }}
          onClick={() => {
            setHasBlockingOrders(false);
            setShowOrders(false);
            setRelatedOrders({ loading: false, orders: [], error: "", hasMore: false });
            setOrderPage(1);
            setCopyNotice(null);
            setRemoving(menu.book);
            setCode("");
            setDialogError("");
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <DeleteOutline color="error" fontSize="small" />
          </ListItemIcon>
          {menu?.book.deleting ? "Concluir exclusão" : "Excluir permanentemente"}
        </MenuItem>
      </Menu>
      <Dialog
        open={Boolean(editing)}
        maxWidth="lg"
        fullWidth
        onClose={() => {
          if (!busy) setEditing(null);
        }}
        aria-labelledby="admin-edit-title"
      >
        <DialogTitle id="admin-edit-title">Editar publicação</DialogTitle>
        <DialogContent
          sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}
        >
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          {editing && (
            <TitleInformationForm
              key={editing.version}
              editing
              inDialog
              reference={editing.reference}
              user={user}
              pdfInfo={editing.pdf}
              paper={editing.paper}
              isSaving={busy}
              onSave={save}
              onClose={() => setEditing(null)}
              draft={{
                ...editing,
                ...editing.details,
                internalCode: editing.reference,
                identificationMode: editing.isbn ? "isbn" : "pso",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(removing)}
        maxWidth="sm"
        fullWidth
        onClose={() => {
          if (!busy) setRemoving(null);
        }}
        aria-labelledby="admin-delete-title"
      >
        <DialogTitle id="admin-delete-title">Excluir publicação permanentemente?</DialogTitle>
        <DialogContent>
          <Typography variant="h6" mb={2}>
            {removing?.title}
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Serão excluídos o cadastro no PSO, o produto na loja, os PDFs de capa e miolo e a imagem
            da capa. Esta ação não pode ser desfeita.
          </Alert>
          <Typography variant="body2" mb={3}>
            O histórico dos pedidos será preservado. Para confirmar, digite o código{" "}
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                verticalAlign: "middle",
              }}
            >
              <strong>{removing?.reference}</strong>
              <Tooltip title="Copiar código PSO">
                <IconButton
                  size="small"
                  aria-label="Copiar código PSO"
                  disabled={busy || !removing?.reference}
                  onClick={async () => {
                    try {
                      if (!navigator.clipboard?.writeText)
                        throw new Error("Clipboard indisponível");
                      await navigator.clipboard.writeText(removing.reference);
                      setCopyNotice({ severity: "success", text: "Código copiado." });
                    } catch {
                      setCopyNotice({
                        severity: "error",
                        text: "Não foi possível copiar. Selecione o código e copie manualmente.",
                      });
                    }
                  }}
                  sx={{ color: "#52616b", minWidth: 44, minHeight: 44 }}
                >
                  <ContentCopyOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              {copyNotice && (
                <Typography
                  component="span"
                  role="status"
                  variant="caption"
                  color={copyNotice.severity === "error" ? "error.main" : "success.main"}
                  sx={{ whiteSpace: copyNotice.severity === "success" ? "nowrap" : "normal" }}
                >
                  {copyNotice.text}
                </Typography>
              )}
            </Box>
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Código PSO da publicação"
            value={code}
            disabled={busy}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="off"
          />
          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
          {hasBlockingOrders && (
            <Box mt={1}>
              <Button
                color="success"
                disabled={relatedOrders.loading}
                onClick={() => {
                  setShowOrders(true);
                  setOrderRevision((value) => value + 1);
                }}
              >
                Ver pedidos
              </Button>
              {showOrders && (
                <Box
                  mt={1}
                  p={2}
                  sx={{ border: "1px solid #dce7df", borderRadius: 2, bgcolor: "#f7faf8" }}
                >
                  <Typography variant="subtitle2" mb={1}>
                    Pedidos em andamento desta publicação
                  </Typography>
                  {relatedOrders.loading ? (
                    <CircularProgress size={22} aria-label="Carregando pedidos da publicação" />
                  ) : (
                    <>
                      {relatedOrders.error && <Alert severity="error">{relatedOrders.error}</Alert>}
                      {!relatedOrders.error && !relatedOrders.orders.length && (
                        <Typography variant="body2">
                          Não há mais pedidos em andamento. Você pode tentar excluir novamente.
                        </Typography>
                      )}
                      {relatedOrders.orders.map((order) => (
                        <Box key={order.id} py={1} sx={{ borderBottom: "1px solid #e2e9e5" }}>
                          <Typography variant="body2" fontWeight={700}>
                            Pedido #{order.id}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Situação: {order.status || "Não informada"} · Pagamento:{" "}
                            {order.paymentStatus || "Não informado"}
                          </Typography>
                        </Box>
                      ))}
                      {!relatedOrders.error && (orderPage > 1 || relatedOrders.hasMore) && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          mt={1}
                        >
                          <Button
                            disabled={orderPage === 1}
                            onClick={() => setOrderPage((value) => value - 1)}
                          >
                            Anterior
                          </Button>
                          <Typography variant="caption">Página {orderPage}</Typography>
                          <Button
                            disabled={!relatedOrders.hasMore}
                            onClick={() => setOrderPage((value) => value + 1)}
                          >
                            Próxima
                          </Button>
                        </Stack>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button disabled={busy} onClick={() => setRemoving(null)}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || code.trim() !== removing?.reference}
            onClick={remove}
          >
            {busy ? "Excluindo..." : "Excluir permanentemente"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
